import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import { Box, IconButton, Typography, Chip } from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TableVirtuoso } from 'react-virtuoso';
import Dropdown from '../../../../Inputs/Dropdown/Dropdown';
import { http } from '../../../../../clients/http/http.client';
import { HTTP_ENDPOINTS, WEB_STORAGE_KEYS } from '../../../../../clients/clients.constants';
import InputText from '../../../../Inputs/InputText/InputText';
import { setTimeZone } from '../../../../../store/timeZoneSlice';
import Loader from '../../../../Inputs/Loader/Loader';
import ExportModal from '../../../../general/ExportModal';
import { Download } from '@mui/icons-material';
import {formatDateTimeInTimeZone, formatDateTimeInTimeZoneObject} from '../../../../../helpers/useTimeZoneTime';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import DateRangeSelector from '../../../../Inputs/DateRangeSelector/DateRangeSelector';
import { WebStorageClient } from '../../../../../clients/webStorage.client';
import { setActionsFrom, setActionsTo } from '../../../../../store/actionsDateFiltersSlice';
import Tooltip from "@mui/material/Tooltip";

const { EQUIPMENT_MOVEMENTS, STORE_SETTINGS, DOWNLOAD_MOVEMENTS_REPORT, GENERATED_FILE } = HTTP_ENDPOINTS;

const columns = [
  { label: 'Site ID', dataKey: 'siteId', width: 150 },
  { label: 'Tag ID', dataKey: 'tagId', width: 200 },
  { label: 'Equipment Type', dataKey: 'equipmentType', width: 190 },
  { label: 'New Location', dataKey: 'location', width: 120 },
  { label: 'Date', dataKey: 'dateTime', width: 100 },
  { label: 'Time', dataKey: 'dateTime', width: 100 },
  { label: 'Duration', dataKey: 'duration', width: 90 },
  { label: 'Store Opening', dataKey: 'storeOpening', width: 150 },
];

const VirtuosoTableComponents = {
  Scroller: React.forwardRef((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
  ),
  TableHead: React.forwardRef((props, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableBody: React.forwardRef((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
  TableRow,
};

function fixedHeaderContent() {
  return (
    <TableRow>
      {columns.map((column) => (
        <TableCell
          key={`${column.dataKey}-${column.label}`}
          variant="head"
          align={column.numeric ? 'right' : 'left'}
          style={{ width: column.width }}
          sx={{
            textTransform: 'uppercase',
            fontSize: '14px',
            fontWeight: '500',
            color: '#718096',
            background: '#fff',
            padding: '8px 10px',
          }}
        >
          {column.label}
        </TableCell>
      ))}
    </TableRow>
  );
}

const defaultPage = 1;

const EquipmentTable = ({ fixtures, matHandling, isMovements, externalLoading }) => {
  const location = useLocation();
  const [isDebounceRunning, setIsDebounceRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState(defaultPage);
  const [isEndReached, setIsEndReached] = useState(false);
  const [sortKey, setSortKey] = useState('dateTime');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState(null);
  const [movementData, setMovementData] = useState([]);
  const [fileGenerationLoading, setIsFileGenerationLoading] = useState(false);
  const initialDateRange = WebStorageClient.getAlertsDateFilters({ forReducer: false });
  const [dateRange, setDateRange] = useState(() => {
    if (initialDateRange && initialDateRange.length === 2) {
      return [new Date(initialDateRange[0]), new Date(initialDateRange[1])];
    }
    const today = new Date();
    return [
      new Date(today.setHours(0, 0, 0, 0)),
      new Date(today.setHours(23, 59, 59, 999)),
    ];
  });

  const dispatch = useDispatch();
  const timeZone = useSelector((state) => state.timeZone.timeZone);
  const alertsDateFilters = useSelector((state) => state.actionsDateFilters);
  const user = useSelector((state) => state.user);
  const siteIdStore = useSelector((state) => state.siteId);

  const cancelTokenSourceRef = useRef(null);
  const requestIdRef = useRef(0);

  const handleSortChange = useCallback((newSortKey) => {
    setSortKey(newSortKey);
  }, []);

  const handleDateRangeChange = (newRange) => {
    if (newRange && newRange.length === 2) {
      const [start, end] = newRange;
      setDateRange([start, end]); // Store Date objects
      const from = DateTime.fromJSDate(start).toISO({ includeOffset: false });
      const to = DateTime.fromJSDate(end).toISO({ includeOffset: false });
      localStorage.setItem(WEB_STORAGE_KEYS.ALERTS_DATE_FILTERS, JSON.stringify([from, to]));
      dispatch(setActionsFrom(from));
      dispatch(setActionsTo(to));
    } else {
      const today = new Date();
      const start = new Date(today.setHours(0, 0, 0, 0));
      const end = new Date(today.setHours(23, 59, 59, 999));
      setDateRange([start, end]);
      const from = DateTime.fromJSDate(start).toISO({ includeOffset: false });
      const to = DateTime.fromJSDate(end).toISO({ includeOffset: false });
      localStorage.setItem(WEB_STORAGE_KEYS.ALERTS_DATE_FILTERS, JSON.stringify([from, to]));
      dispatch(setActionsFrom(from));
      dispatch(setActionsTo(to));
    }
  };

  const formatDuration = useCallback((totalMinutes) => {
    const days = Math.floor(totalMinutes / 1440);
    const remainder = totalMinutes % 1440;
    const hours = Math.floor(remainder / 60);
    const minutes = Math.floor(remainder % 60);
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  }, []);

  const getCellColour = useCallback((column, row) => {
    return '#2D3748';
  }, []);

  const getRowContent = (column, row) => {
    if (column.label === 'Date') {
      return siteIdStore?.siteId ? row?.date : formatDateTimeInTimeZoneObject(row?.dateTime)?.date;
    }
    if (column.label === 'Time') {
      return siteIdStore?.siteId ? row?.time : formatDateTimeInTimeZoneObject(row?.dateTime)?.time;
    }

    if (column.label === 'New Location') {
      return (
        <Box key={row?.Movement_ID} sx={{ display: 'flex', alignItems: 'center' }}>
          <span style={{textWrap: 'nowrap'}}>{row[column.dataKey]}</span>
          {row?.isLastMovement === 1 ? (
            <Tooltip title={`${row.equipmentType} is currently in this location`} placement="top" arrow>
              <Chip sx={{ fontSize:"8px", background: '#1B3665', fontWeight: '700', height: "18px!important", ml: '13px', '.MuiChip-label': {padding: '1px 5px 0 5px'}, transform: "translateY(-1px)" }} label="Current" size="small" color="primary"/>
            </Tooltip>
          ) : ''}
        </Box>
      )
    }

    if (column.label === 'Duration') {
      if (row[column.dataKey]) {
        return formatDuration(row[column.dataKey]);
      } else {
        const rowDateTime = DateTime.fromISO(row.dateTime);
        const now = DateTime.now();
        const diffInMinutes = now.diff(rowDateTime, 'minutes').minutes;
        // return formatDuration(diffInMinutes);
        return '';
      }
    }
    return row[column.dataKey];
  }

  const rowContent = (index, row) => (
      <>
        {columns.map((column) => (
          <TableCell
            key={`${column.dataKey}-${column.label}`}
            align={column.numeric ? 'right' : 'left'}
            sx={{
              fontSize: '12px',
              fontWeight: '500',
              padding: '8px 10px',
              color: getCellColour(column, row),
            }}
          >
            {getRowContent(column, row)}
          </TableCell>
        ))}
      </>
    );

  useEffect(() => {
    setPage(defaultPage);
    setMovementData([]);
    setIsEndReached(false);
  }, [location.search]);

  // Modify buildSiteIdQueryParams to accept currentPage
  const buildSiteIdQueryParams = useCallback((currentPage) => {
    const params = new URLSearchParams();
    if (alertsDateFilters?.from && alertsDateFilters?.to) {
      const fromDate = DateTime.fromISO(alertsDateFilters.from);
      const toDate = DateTime.fromISO(alertsDateFilters.to);
      if (fromDate.isValid && toDate.isValid) {
        params.append('from', fromDate.toISO({ includeOffset: false }));
        params.append('to', toDate.toISO({ includeOffset: false }));
      }
    }
    if (siteIdStore.siteId) {
      params.append('siteId', location.search.includes('arrc-inventory') ? 1 : siteIdStore.siteId);
    } else {
      if (location.search.includes('arrc-inventory')) {
        params.append('siteId', location.search.includes('arrc-inventory') ? 1 : siteIdStore.siteId);
      }
    }

    if (search) {
      params.append('search', search);
    } else {
      params.delete('search');
    }
    params.append('sortDirection', sortOrder);
    if (fixtures && fixtures.length > 0) {
      params.append('equipmentTypeIds', fixtures.join(','));
    }
    if (matHandling && matHandling.length > 0) {
      params.append('location', matHandling.join(','));
    }
    if (sortKey) {
      params.append('sortField', sortKey);
    } else {
      params.delete('sortField');
    }
    params.append('page', currentPage);
    params.append('limit', limit);
    return params.toString() ? `?${params.toString()}` : '';
  }, [alertsDateFilters, siteIdStore.siteId, location.search, search, sortOrder, fixtures, matHandling, sortKey, limit]);

  const fetchData = useCallback(async () => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel('Cancelled due to new request');
    }
    const source = axios.CancelToken.source();
    cancelTokenSourceRef.current = source;
    const currentRequestId = ++requestIdRef.current;

    try {
      setIsLoading(true);
      setPage(defaultPage);
      setMovementData([]);
      setIsEndReached(false);

      if (siteIdStore?.siteId) {
        const storeSettingsResponse = await http.client.get(`${STORE_SETTINGS}/${siteIdStore.siteId}`, {
          cancelToken: source.token,
        });
        dispatch(setTimeZone(storeSettingsResponse.TimeZone));
      } else {
        dispatch(setTimeZone(null));
      }

      const filters = buildSiteIdQueryParams(defaultPage);
      const res = await http.client.get(`${EQUIPMENT_MOVEMENTS}/${filters}`, {
        cancelToken: source.token,
      });

      if (currentRequestId === requestIdRef.current) {
        setMovementData(res.data);
        setIsEndReached(res.data.length === 0);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Error fetching movements:', error);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
      cancelTokenSourceRef.current = null;
    }
  }, [buildSiteIdQueryParams, dispatch, siteIdStore.siteId, location.search]);

  const fetchDataPaginated = async () => {
    try {
      setIsScrollLoading(true);
      const filters = buildSiteIdQueryParams(page);
      const res = await http.client.get(`${EQUIPMENT_MOVEMENTS}/${filters}`);
      setMovementData((prevState) => [...prevState, ...res.data]);
      setIsEndReached(res.data.length === 0);

      setIsScrollLoading(false);
    } catch (error) {
      setIsScrollLoading(false);
    }
  }

// Remove this redundant useEffect
  useEffect(() => {
    setPage(defaultPage);
    setMovementData([]);
    setIsEndReached(false);
  }, [location.search]);

// Existing useEffect for fetchData (unchanged dependencies)
  useEffect(() => {
    if (externalLoading) return;
    fetchData();
    return () => {
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Component unmounted');
        cancelTokenSourceRef.current = null;
      }
    };
  }, [
    fetchData,
    alertsDateFilters.from,
    alertsDateFilters.to,
    search,
    siteIdStore.siteId,
    sortKey,
    sortOrder,
    fixtures,
    matHandling,
    location.search,
    externalLoading,
  ]);

  useEffect(() => {
    if (page !== defaultPage && !isScrollLoading) {
      fetchDataPaginated();
    }
  }, [page]);

  const checkGeneratedFile = useCallback(async () => {
    try {
      const email = user.user.preferred_username;
      const dataType = 'movements';
      const res = await http.client.get(`${GENERATED_FILE}?dataType=${dataType}&email=${encodeURIComponent(email)}`);
      setGeneratedFiles(res.files && res.files.length > 0 ? res.files : []);
    } catch (error) {
      console.error('Error checking generated file:', error);
      setGeneratedFiles([]);
    }
  }, [user.user.email]);

  const openModal = useCallback(async () => {
    setModalOpen(true);
    await checkGeneratedFile();
  }, [checkGeneratedFile]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleAllMovementsDownload = useCallback(
    async (fileType) => {
      const source = axios.CancelToken.source();
      cancelTokenSourceRef.current = source;
      try {
        setIsFileGenerationLoading(true);
        const filters = buildSiteIdQueryParams();
        await http.client.get(`${DOWNLOAD_MOVEMENTS_REPORT}/${filters}&fileType=${fileType.toLowerCase()}`, {
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setExportProgress(percentCompleted);
              setIsExporting(true);
            }
          },
          cancelToken: source.token,
        });
        setExportProgress(100);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error during export:', error);
        }
      } finally {
        setIsExporting(false);
        setIsFileGenerationLoading(false);
        setExportProgress(0);
        cancelTokenSourceRef.current = null;
        checkGeneratedFile();
      }
    },
    [buildSiteIdQueryParams, checkGeneratedFile]
  );

  const cancelExport = useCallback(() => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel('Export canceled by user');
      cancelTokenSourceRef.current = null;
    }
  }, []);

  const getTableHeight = useCallback(() => {
    if (location.search.includes('backroom-material')) {
      return 'calc(100vh - 390px)';
    }
    if (location.search.includes('fixtures') || location.search.includes('arrc-inventory') || location.search.includes('shopping-carts')) {
      return 'calc(100vh - 390px)';
    }
    return '325px';
  }, [location.search]);

  useEffect(() => {
    console.log('isScrollLoading',isScrollLoading);
    console.log('isEndReached',isEndReached);
  }, [isScrollLoading, isEndReached]);

  return (
    <Paper
      sx={{
        width: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0px 4px 40px rgba(226, 236, 249, 0.60)',
        marginTop: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E5E5',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#1B3665' }}>
          Equipment Movements
        </Typography>
        <Box>
          <DateRangeSelector value={dateRange} onChange={handleDateRangeChange} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <InputText
            value={search}
            onChangeFn={setSearch}
            withDebounce={true}
            debounceDelay={1000}
            onDebounceEnd={() => setIsDebounceRunning(false)}
            onDebounceStart={() => setIsDebounceRunning(true)}
          />
          <Dropdown
            value={sortKey}
            onChangeFn={setSortKey}
            options={columns.filter((col) => col.label !== 'Time').map((col) => ({ name: col.label, value: col.dataKey }))}
            placeHolder="Sort by:"
            widthString="200px"
            withDebounce={true}
            onDebounceEnd={() => setIsDebounceRunning(false)}
            onDebounceStart={() => setIsDebounceRunning(true)}
          />
          <Dropdown
            value={sortOrder}
            onChangeFn={setSortOrder}
            options={[
              { name: 'Ascending', value: 'ASC' },
              { name: 'Descending', value: 'DESC' },
            ]}
            placeHolder="Order:"
            widthString="200px"
            withDebounce={true}
            onDebounceEnd={() => setIsDebounceRunning(false)}
            onDebounceStart={() => setIsDebounceRunning(true)}
          />
          <IconButton aria-label="download" color="primary" onClick={openModal}>
            <Download />
          </IconButton>
          <ExportModal
            open={modalOpen}
            onClose={closeModal}
            fileName={`movements_report_${Date.now()}`}
            handleDownloadAll={handleAllMovementsDownload}
            onCancelExport={cancelExport}
            isMovements
            progress={exportProgress}
            generatedFiles={generatedFiles}
            fileGenerationLoading={fileGenerationLoading}
            isExporting={isExporting}
          />
        </Box>
      </Box>
      <Paper style={{ height: getTableHeight(), width: '100%', boxShadow: 'none' }}>
        {isLoading || isDebounceRunning || externalLoading ? (
          <Loader sx={{ height: '337px' }} />
        ) : (
          <TableVirtuoso
            data={movementData}
            components={VirtuosoTableComponents}
            fixedHeaderContent={fixedHeaderContent}
            itemContent={rowContent}
            endReached={() => {
              if (!isScrollLoading && !isEndReached) {
                console.log('Fetching page:', page + 1);
                setPage((prevState) => prevState + 1);
              }
            }}
          />
        )}
      </Paper>
    </Paper>
  );
};

export default EquipmentTable;